import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StringHelper } from '@core/helpers/string.helper';
import { Estimate } from '@interfaces/Estimate';
import { ExportProjectRequestDto } from '@interfaces/ExportProjectRequestDTO';
import { ImportProjectDto } from '@interfaces/ImportProjectDTO';
import { ImportProjectRequestDto } from '@interfaces/ImportProjectRequestDTO';
import { BaseService } from '@shared/services/base.service';
import { LoadingService } from '@shared/services/loading.service';
import { Subject, from, lastValueFrom, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EstimateImportService extends BaseService {
  public constructor(private http: HttpClient, private loadingService: LoadingService) {
    super();
  }

  public async import(request: ImportProjectRequestDto) {
    const estimateId = await this.loadingService.run(() => {
      return lastValueFrom(this.http.post<string>(`${this.baseUrl}/web/estimateImport/import`, request));
    });

    await Promise.all([this.refreshEstimates(), this.refreshGetProjectsForImport()]);

    return estimateId;
  }

  public async exportToPc(request: ExportProjectRequestDto) {
    const estimateId = await this.loadingService.run(() => {
      return lastValueFrom(this.http.post<string>(`${this.baseUrl}/web/estimateImport/export`, request));
    });

    await Promise.all([this.refreshEstimates(), this.refreshGetProjectsForImport()]);
    return estimateId;
  }

  private getProjectCodesNotInEstimate$ = new Subject<ImportProjectDto[]>();
  public getProjectCodesNotInEstimate(refresh = true) {
    if (refresh) {
      return from(this.refreshGetProjectsForImport()).pipe(
        switchMap(() => this.getProjectCodesNotInEstimate$.asObservable())
      );
    }

    return this.getProjectCodesNotInEstimate$.asObservable();
  }

  public async refreshGetProjectsForImport() {
    const projects = await lastValueFrom(
      this.http.get<ImportProjectDto[]>(`${this.baseUrl}/web/estimateImport/project`)
    );

    this.getProjectCodesNotInEstimate$.next(projects);
    return projects;
  }

  public async canExportEstimate(estimateGroupId: string): Promise<string | undefined> {
    let error: string | undefined = undefined;

    try {
      await lastValueFrom(
        this.http.get(`${this.baseUrl}/web/estimateexport/canExport?estimateGroupId=${estimateGroupId}`)
      );
    } catch (ex: unknown) {
      const httpErrorResponse = ex as HttpErrorResponse;
      error = 'ERROR.CODE.' + (httpErrorResponse.error as string);
    }

    return error;
  }

  private getEstimates$ = new Subject<Estimate[]>();

  public getEstimates(refresh = true) {
    if (refresh) {
      this.refreshEstimates();
    }

    return this.getEstimates$.asObservable();
  }

  public async refreshEstimates() {
    const estimates = await lastValueFrom(this.http.get<Estimate[]>(`${this.baseUrl}/web/estimateExport/estimate`));
    estimates.forEach(e => {
      if (!StringHelper.HasValue(e.codeRef)) {
        e.codeRef = undefined; // patches an issue where CodeRef is set to ' '
      }
    });
    this.getEstimates$.next(estimates);
    return estimates;
  }
}
